import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Edit, AlertTriangle, Clock, Calendar, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { PrioridadesService } from '../../services';
import Modal from '../../components/ui/Modal';

interface Priority {
  id: number;
  nome: string;
  cor: string;
  descricao: string;
  sla_valor: number;
  sla_unidade: string;
  created_at?: string;
  updated_at?: string;
}

interface Filters {
  nome: string;
  sla_unidade: string;
}

interface ModalData {
  isOpen: boolean;
  mode: 'edit';
  priority?: Priority;
}

const Priorities: React.FC = () => {
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<Filters>({
    nome: 'all',
    sla_unidade: 'all'
  });
  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    mode: 'edit'
  });
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    sla_valor: '',
    sla_unidade: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadPriorities();
  }, []);

  const loadPriorities = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await PrioridadesService.getPrioridades();
      setPriorities(response as Priority[]);
    } catch (error) {
      console.error('Erro ao carregar prioridades:', error);
      toast.error('Erro ao carregar prioridades');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPriorities = priorities.filter(priority => {
    const matchesSearch = priority.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         priority.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesNome = filters.nome === 'all' || 
                       priority.nome === filters.nome;
    
    const matchesSlaUnidade = filters.sla_unidade === 'all' || 
                             priority.sla_unidade === filters.sla_unidade;
    
    return matchesSearch && matchesNome && matchesSlaUnidade;
  });

  const openEditModal = (priority: Priority) => {
    setModalData({ isOpen: true, mode: 'edit', priority });
    setFormData({
      nome: priority.nome || '',
      descricao: priority.descricao || '',
      sla_valor: priority.sla_valor?.toString() || '',
      sla_unidade: priority.sla_unidade || ''
    });
  };

  const closeModal = () => {
    setModalData({ isOpen: false, mode: 'edit' });
    setFormData({ nome: '', descricao: '', sla_valor: '', sla_unidade: '' });
    setIsSubmitting(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim() || !formData.descricao.trim() || !formData.sla_valor || !formData.sla_unidade) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    setIsSubmitting(true);

    try {
      if (modalData.priority) {
        await PrioridadesService.updatePrioridade(modalData.priority.id, {
          nome: formData.nome.trim(),
          descricao: formData.descricao.trim(),
          sla_valor: parseInt(formData.sla_valor),
          sla_unidade: formData.sla_unidade as 'horas' | 'dias',
          // manter a cor atual para compatibilidade com o backend
          cor: modalData.priority.cor
        });
        toast.success('Prioridade atualizada com sucesso!');
      }

      closeModal();
      loadPriorities(); // Recarregar lista
    } catch (error: any) {
      console.error('Erro ao atualizar prioridade:', error?.response || error);
      toast.error(error.message || 'Erro ao atualizar prioridade');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Prioridades
            </h1>
            <p className="text-gray-600 dark:text-gray-400 dark:text-gray-500">
              Gerencie as prioridades dos tickets e seus SLAs
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar prioridades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Nome Filter */}
          <select
            value={filters.nome}
            onChange={(e) => setFilters({...filters, nome: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
          >
            <option value="all">Todos os nomes</option>
            {Array.from(new Set(priorities.map(p => p.nome))).filter(nome => nome).map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </select>

          {/* SLA Unidade Filter */}
          <select
            value={filters.sla_unidade}
            onChange={(e) => setFilters({...filters, sla_unidade: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
          >
            <option value="all">Todas as unidades</option>
            {Array.from(new Set(priorities.map(p => p.sla_unidade))).filter(unidade => unidade).map(unidade => (
              <option key={unidade} value={unidade}>{unidade}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Priorities Table */}
      <div className="bg-white dark:bg-gray-800/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  SLA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Data de Criação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPriorities.length > 0 ? (
                filteredPriorities.map((priority) => (
                  <tr key={priority.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:bg-gray-900/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center mr-3">
                          <span className="text-primary-700 dark:text-primary-300 font-medium text-sm">
                            {priority.nome.charAt(0) || 'P'}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {priority.nome}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                        {priority.descricao || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {priority.sla_valor} {priority.sla_unidade}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                        {priority.created_at ? new Date(priority.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(priority)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 dark:text-gray-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-lg font-medium">Nenhuma prioridade encontrada</p>
                    <p className="text-sm">Tente ajustar os filtros de busca</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para Editar Prioridade */}
      {modalData.isOpen && (
        <Modal
          isOpen={modalData.isOpen}
          onClose={closeModal}
          title="Editar Prioridade"
          maxWidth="md"
          closeOnBackdropClick={!isSubmitting}
        >

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Nome */}
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
                  placeholder="Digite o nome da prioridade"
                  required
                />
              </div>

              {/* Descrição */}
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição *
                </label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
                  placeholder="Digite a descrição da prioridade"
                  required
                />
              </div>

              {/* SLA Valor */}
              <div>
                <label htmlFor="sla_valor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor do SLA *
                </label>
                <input
                  type="number"
                  id="sla_valor"
                  name="sla_valor"
                  value={formData.sla_valor}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
                  placeholder="Digite o valor do SLA"
                  required
                />
              </div>

              {/* SLA Unidade */}
              <div>
                <label htmlFor="sla_unidade" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Unidade do SLA *
                </label>
                <select
                  id="sla_unidade"
                  name="sla_unidade"
                  value={formData.sla_unidade}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500"
                  required
                >
                  <option value="">Selecione a unidade</option>
                  <option value="horas">Horas</option>
                  <option value="dias">Dias</option>
                </select>
              </div>

              {/* Botões */}
              <div className="flex items-center space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:bg-gray-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary-500 dark:focus:ring-primary-400 dark:focus:ring-primary-400"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 border border-transparent rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary-500 dark:focus:ring-primary-400 dark:focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Salvando...
                    </div>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default Priorities;
